// Errores de agregar, quitar o cambiar un producto de la venta → mensaje y acción del vendedor.
// Cada code dice su causa: un catch ciego mandaba a elegir otro equipo ante cualquier falla.
import { RequestTimeoutError } from '@/api/client';
import { ApiError } from '@/api/errors';
import { componentSkuOf, lineErrorMessage } from './lineErrors';

/**
 * Un mensaje por código, en el orden en que el vendedor los vive.
 *
 * Los códigos salen de los errores tipados del silo:
 * `retail-wizard-business/src/components/errors/*.ts` (`code` + `httpStatus` del `DomainError`).
 */
const MESSAGE_BY_CODE: Record<string, string> = {
    // El SKU se quedó sin unidades entre que se pintó el catálogo y el clic. Es la carrera REAL.
    PRODUCT_NOT_AVAILABLE:
        'Este producto se acaba de agotar. Elige otro; el catálogo ya se actualizó.',
    // El motor de crédito no respondió al descartar la cotización: no se tocó nada.
    EXTERNAL_INTEGRATION_ERROR:
        'El sistema de crédito no respondió y la venta quedó igual. Espera unos segundos y vuelve a intentar.',
    // Tras activar el crédito las líneas ya no cambian; lo que sigue es entregar.
    CREDIT_NOT_PENDING:
        'El crédito de esta venta ya se activó: ya no puedes cambiar los productos. Sigue con la entrega.',
    // El vendedor no está dado de alta en retail-org: ninguna venta le puede ser atribuida.
    SELLER_NOT_REGISTERED:
        'Tu usuario no está dado de alta como vendedor de esta tienda. Avisa a tu gerente.',
    // La saga ya no está ACTIVE (cancelada, vencida o cerrada en otra tablet).
    INVALID_SESSION_STATE: 'Esta venta ya no está activa. Vuelve al portal e inicia una nueva.',
    SESSION_CANCELLED: 'Esta venta ya no está activa. Vuelve al portal e inicia una nueva.',
    SESSION_NOT_FOUND: 'Esta venta ya no existe. Vuelve al portal e inicia una nueva.',
    PERMISSION_DENIED: 'No tienes permiso para vender en esta tienda.',
    VALIDATION_ERROR:
        'El producto elegido no se pudo enviar al sistema. Vuelve a cargar el catálogo.',
};

const FALLBACK = 'No se pudo apartar el producto. Vuelve a intentar en unos segundos.';

/** `change-phone` = «Cambiar» el teléfono · `delivery` = seguir con la venta (Referidos o Entrega) · `retry` = reintentar. */
export type LineChangeAction = 'change-phone' | 'delivery' | 'retry';

const ACTION_BY_CODE: Record<string, LineChangeAction> = {
    SALE_PHONE_LIMIT_REACHED: 'change-phone',
    CREDIT_NOT_PENDING: 'delivery',
    EXTERNAL_INTEGRATION_ERROR: 'retry',
    CATALOG_UNAVAILABLE: 'retry',
};

/** Etiqueta del botón de cada acción, igual en «Elige el teléfono» y «Arma la venta». */
export const LINE_CHANGE_ACTION_LABEL: Record<LineChangeAction, string> = {
    'change-phone': 'Cambiar',
    delivery: 'Continuar la venta',
    retry: 'Reintentar',
};

/** Acción que resuelve el error; `undefined` si basta con el mensaje y releer la venta. */
export function lineChangeAction(error: unknown): LineChangeAction | undefined {
    if (!(error instanceof ApiError) || !Object.hasOwn(ACTION_BY_CODE, error.code))
        return undefined;
    return ACTION_BY_CODE[error.code];
}

/**
 * Mensaje accionable para el vendedor.
 *
 * ⚠️ NO cae al `error.msg` del backend por defecto: esos mensajes están escritos para el log
 * («Transición inválida de la saga: la sesión está en estado CANCELLED, no ACTIVE») y frente a un
 * cliente no dicen qué hacer. Solo se usa el del backend cuando trae un código que este front no
 * conoce todavía — ahí un texto crudo es mejor que un genérico mudo.
 */
export function reservationErrorMessage(error: unknown): string {
    if (error instanceof RequestTimeoutError) {
        return 'La reserva tardó demasiado. Revisa los productos de la venta antes de volver a intentar: puede haber quedado apartado.';
    }
    if (!(error instanceof ApiError)) return FALLBACK;

    const lineMessage = lineErrorMessage(error);
    if (lineMessage !== undefined) return lineMessage;
    const known = MESSAGE_BY_CODE[error.code] as string | undefined;
    if (known !== undefined) return known;

    // Un 401/403 sin código Fiado es el authorizer, no el catálogo.
    if (error.httpStatus === 401 || error.httpStatus === 403) {
        return 'Tu sesión perdió permisos sobre esta tienda. Vuelve a iniciar sesión.';
    }
    return error.msg !== '' ? error.msg : FALLBACK;
}

const SWAP_KEEPS = 'El equipo de la venta se queda igual.';

/** Mensaje de «Cambiar» o «Cambiar por el kit»: sin existencia, la ancla anterior se queda (REQ-84). */
export function swapErrorMessage(error: unknown): string {
    if (error instanceof ApiError && error.code === 'INSUFFICIENT_STOCK') {
        return `Ya no hay existencia de ese equipo en la tienda. ${SWAP_KEEPS}`;
    }
    if (error instanceof ApiError && error.code === 'COMPONENT_OUT_OF_STOCK') {
        const sku = componentSkuOf(error);
        const missing =
            sku !== undefined ? `A este kit le falta ${sku}` : 'A este kit le falta un componente';
        return `${missing} en la tienda. ${SWAP_KEEPS}`;
    }
    return reservationErrorMessage(error);
}

/** Toast cuando el inventario no respondió al renovar: no se escribió nada. */
export const RENEW_UNAVAILABLE_MESSAGE = 'No pudimos renovar el apartado. Intenta de nuevo.';

/** `replace` = volver a apartar con replacesLineId · `add` = la línea ya no está, se agrega nueva. */
export type RenewFallback = 'replace' | 'add' | 'unavailable';

const RENEW_FALLBACK_BY_CODE: Record<string, RenewFallback> = {
    RESERVATION_NOT_RENEWABLE: 'replace',
    LINE_NOT_FOUND: 'add',
    CATALOG_UNAVAILABLE: 'unavailable',
};

/** Qué hacer si falla la renovación; `undefined` si se trata como un error de apartar. */
export function renewFallbackOf(error: unknown): RenewFallback | undefined {
    if (!(error instanceof ApiError) || !Object.hasOwn(RENEW_FALLBACK_BY_CODE, error.code))
        return undefined;
    return RENEW_FALLBACK_BY_CODE[error.code];
}
